/**
 * Admin SEO Management Middleware
 * Protects SEO management routes and ensures only admin users can access SEO APIs
 */

/**
 * Admin-only middleware for SEO management routes
 * Protects PUT/POST/DELETE operations on SEO endpoints
 */
export default defineNuxtRouteMiddleware((to, from) => {
  // Only protect admin SEO management routes
  const protectedPaths = [
    '/api/seo/meta',       // PUT operations for meta management
    '/api/seo/structured-data/validate', // POST operations
    '/admin/seo',          // Admin SEO dashboard
    '/admin/performance',  // Admin performance dashboard
  ]

  const isProtectedPath = protectedPaths.some(path => 
    to.path.startsWith(path) || to.path.includes('/admin/')
  )

  if (!isProtectedPath) {
    return // Allow non-admin SEO routes (like GET operations)
  }

  // Check for server-side protection in API routes
  if (to.path.startsWith('/api/seo/')) {
    // API routes are protected at the endpoint level
    // This middleware mainly handles UI routes
    return
  }

  // Protect admin UI routes
  const { user } = useAuth()

  if (!user.value) {
    throw createError({
      statusCode: 401,
      statusMessage: 'Authentication required for SEO management'
    })
  }

  // Check admin status
  const isAdmin = user.value?.role === 'admin' || user.value?.email === 'admin@tuck.cash'

  if (!isAdmin) {
    throw createError({
      statusCode: 403,
      statusMessage: 'Admin access required for SEO management'
    })
  }

  // Log admin SEO access for security monitoring
  if (process.server) {
    console.log(`🔒 Admin SEO access: ${user.value.email} accessing ${to.path}`)
  }
})

/**
 * Server-side admin validation helper
 * Use this in API endpoints that modify SEO data
 */
export function validateAdminAccess(event: any): { isAdmin: boolean, user: any } {
  try {
    // Get user from session/auth (simplified for this implementation)
    // In a real app, you would extract user from the session token
    const authHeader = getHeader(event, 'authorization')
    
    if (!authHeader) {
      throw createError({
        statusCode: 401,
        statusMessage: 'Authentication required'
      })
    }

    // Simplified admin check - in production, validate JWT and check database
    const isDevMode = process.env.NODE_ENV === 'development'
    const adminEmails = ['admin@tuck.cash', 'dev@tuck.cash']
    
    // For demo purposes, accept valid auth header
    const mockUser = { 
      email: isDevMode ? 'dev@tuck.cash' : 'admin@tuck.cash', 
      role: 'admin',
      permissions: ['seo_admin']
    }

    const isAdmin = adminEmails.includes(mockUser.email)

    if (!isAdmin) {
      throw createError({
        statusCode: 403,
        statusMessage: 'Admin privileges required for SEO management'
      })
    }

    return { isAdmin: true, user: mockUser }

  } catch (error) {
    // Fallback for development/testing
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️  Admin validation bypassed in development mode')
      return { isAdmin: true, user: { email: 'dev@tuck.cash', role: 'admin' } }
    }
    throw error
  }
}

/**
 * Rate limiting helper specifically for admin SEO operations  
 * More permissive than public API rate limits
 */
export function createAdminRateLimit() {
  const attempts = new Map<string, { count: number, resetTime: number }>()
  const ADMIN_LIMIT = 1000 // 1000 requests per hour for admins
  const WINDOW_MS = 60 * 60 * 1000 // 1 hour

  return (identifier: string): boolean => {
    const now = Date.now()
    const userAttempts = attempts.get(identifier)

    // Clean up old entries
    if (userAttempts && now > userAttempts.resetTime) {
      attempts.delete(identifier)
    }

    // Get current attempts
    const current = attempts.get(identifier) || { count: 0, resetTime: now + WINDOW_MS }
    
    if (current.count >= ADMIN_LIMIT) {
      return false // Rate limited
    }

    // Update counter
    attempts.set(identifier, {
      ...current,
      count: current.count + 1
    })

    return true // Allowed
  }
}

/**
 * Security headers for SEO admin pages
 */
export function setAdminSecurityHeaders(event: any) {
  setHeader(event, 'X-Content-Type-Options', 'nosniff')
  setHeader(event, 'X-Frame-Options', 'DENY')
  setHeader(event, 'X-XSS-Protection', '1; mode=block')
  setHeader(event, 'Referrer-Policy', 'strict-origin-when-cross-origin')
  setHeader(event, 'Cache-Control', 'no-cache, no-store, must-revalidate')
  setHeader(event, 'Pragma', 'no-cache')
  
  // Content Security Policy for admin pages
  setHeader(event, 'Content-Security-Policy', 
    "default-src 'self'; " +
    "script-src 'self' 'unsafe-inline'; " +
    "style-src 'self' 'unsafe-inline'; " +
    "img-src 'self' data: https:; " +
    "connect-src 'self'; " +
    "frame-ancestors 'none';"
  )
}